import React, { useState } from "react";
import { usePagination } from "../../../hooks/usePagination";
import ResultHandler from "../../../components/ResultHandler";
import PaginationHandler from "../../../components/PaginationHandler";
import TableHeaderHandler from "../../../components/TableHeaderHandler";
import { useTranslation } from "react-i18next";
import EmptyData from "../../../components/EmptyData";
import { Table, Thead, Tbody, Td, Tr, Th } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useResponse } from "../../../hooks/useResponse";
import DeleteModal from "../../../components/Common/DeleteModal";
import { Badge, Button, Col, Label, Row } from "reactstrap";
import {
  cancelReservation,
  deAssignReservation,
  getReservations,
} from "../../../api/reservation";
import { ZoomImage } from "../../../components/zoom-image/ZoomImage";
import moment from "moment";
import { useHistory } from "react-router-dom";
import TableFilter from "../../../components/TableFilter";
import DeAssignReservationModal from "../../../components/reservation-modal/DeAssignReservationModal";
import { formatCurrency } from "../../../utils/formatCurrency";

export default function RoomReservationsTable() {
  const { t } = useTranslation();
  const navigate = useHistory();

  const {
    response,
    loading,
    error,
    setPage,
    page,
    setPerPage,
    totalPages,
    setPayload,
    setMeta,
  } = usePagination(getReservations, { type: "room" });

  // Filter
  const filterTypes = [
    { key: "startAt", label: "From", type: "date" },
    { key: "endAt", label: "To", type: "date" },
  ];

  // De Assign
  const [showDeAssignModal, setShowDeAssignModal] = useState(false);
  const [deAssignedEntity, setDeAssignedEntity] = useState(null);

  const { setData: setDeAssignedData, error: deAssignError } = useResponse(
    deAssignReservation,
    () => {
      setShowDeAssignModal(false);
      setDeAssignedEntity(null);
      setPayload({ ref: true, type: "room" });
    }
  );

  function onDeAssignConfirm(date) {
    setDeAssignedData({ type: "edit", id: deAssignedEntity, data: { date } });
  }

  function onDeAssignCancel() {
    setShowDeAssignModal(false);
  }

  // Cancel
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceledEntity, setCanceledEntity] = useState(null);

  const { setData: setDeCanceledData, error: cancelError } = useResponse(
    cancelReservation,
    () => {
      setShowCancelModal(false);
      setCanceledEntity(null);
      setPayload({ ref: true, type: "room" });
    }
  );

  function onCancelConfirm() {
    setDeCanceledData({ type: "delete", data: canceledEntity });
  }

  function onCancelCancel() {
    setShowCancelModal(false);
  }

  return (
    <>
      <TableHeaderHandler
        setPage={setPage}
        setPerPage={setPerPage}
      ></TableHeaderHandler>

      <TableFilter
        filterTypes={filterTypes}
        onFilter={(filters) => setMeta({ page: 1, payload: { ...filters, type: "room" } })}
      />

      <div className="table-rep-plugin">
        <div className="table-responsive">
          <Table className="table table-striped table-bordered">
            <Thead className="table-light table-nowrap">
              <Tr>
                <Th>Room Number</Th>
                <Th>Street Name</Th>
                <Th>Street Number</Th>
                <Th>Renter</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Amount</Th>
                <Th>Last Paid</Th>
                <Th>Picture</Th>
                <Th className="d-flex">{t("tables.Action")}</Th>
              </Tr>
            </Thead>

            {response.data && !loading && (
              <Tbody>
                {response.data.reservations.map((row) => {
                  return (
                    <Tr key={row.id}>
                      <Td>{row.room?.room_number}</Td>
                      <Td>{row.room?.housing?.street_name}</Td>
                      <Td>{row.room?.housing?.number}</Td>
                      <Td>
                        {row.employee_id ? (
                          <>
                            {row.employee?.name}{" "}
                            <Badge className="p-1 px-2 ms-2" color="info">
                              Employee
                            </Badge>
                          </>
                        ) : row.admin_id ? (
                          <>
                            {row.admin?.name}{" "}
                            <Badge className="p-1 px-2 ms-2" color="info">
                              Admin
                            </Badge>
                          </>
                        ) : row.contract_id ? (
                          <>
                            {row.contract?.company?.name}{" "}
                            <Badge className="p-1 px-2 ms-2" color="info">
                              Company
                            </Badge>
                          </>
                        ) : (
                          ""
                        )}
                      </Td>
                      <Td>
                        {row.startAt &&
                          moment(row.startAt).format("DD MMM YYYY")}
                      </Td>
                      <Td>
                        {row.endAt && moment(row.endAt).format("DD MMM YYYY")}
                      </Td>
                      <Td className="text-nowrap">
                        {formatCurrency(row.amount)}
                      </Td>
                      <Td>
                        {row.last_paid &&
                          moment(row.last_paid).format("DD MMM YYYY")}
                      </Td>
                      <Td>
                        {row.room.picture && (
                          <ZoomImage
                            src={row.room.picture}
                            alt="room image"
                            width={150}
                          />
                        )}
                      </Td>
                      <Td className="d-flex">
                        <Button
                          style={{ marginLeft: "5px" }}
                          color="dark"
                          onClick={() =>
                            navigate.push("/reservations/" + row.id)
                          }
                        >
                          <i className="bx bxs-show font-size-16 align-middle" />
                        </Button>

                        {row.startAt > new Date().toISOString() &&
                          row.endAt > new Date().toISOString() && (
                            <Button
                              color="danger"
                              style={{ marginLeft: "5px" }}
                              className="fw-bold text-nowrap"
                              onClick={() => {
                                setShowCancelModal(true);
                                setCanceledEntity(row.id);
                              }}
                            >
                              Cancel
                            </Button>
                          )}

                        {(row.endAt === null ||
                          (row.startAt < new Date().toISOString() &&
                            row.endAt >= new Date().toISOString())) && (
                          <Button
                            color="danger"
                            style={{ marginLeft: "5px" }}
                            className="fw-bold text-nowrap"
                            onClick={() => {
                              setShowDeAssignModal(true);
                              setDeAssignedEntity(row.id);
                            }}
                          >
                            De Assign
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            )}
          </Table>

          {!loading && (
            <EmptyData
              empty={response.data && response.data.reservations.length === 0}
              msg="No Reservations Yet."
            />
          )}

          {response.data && !loading && (
            <PaginationHandler
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          )}

          <ResultHandler error={error} loading={loading} />

          {/* De Assign */}
          <DeAssignReservationModal
            show={showDeAssignModal}
            onConfirm={onDeAssignConfirm}
            onCloseClick={onDeAssignCancel}
            error={deAssignError}
          />

          {/* Cancel */}
          <DeleteModal
            show={showCancelModal}
            onDeleteClick={onCancelConfirm}
            onCloseClick={onCancelCancel}
            error={cancelError}
          />
        </div>
      </div>
    </>
  );
}
